/**
 * SignUpForm — email + password + confirm + strength meter + Turnstile.
 */
import { useState } from "react";
import { UserPlus, Eye, EyeOff, Check, XIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  onSignUp: (email: string, password: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

export function SignUpForm({ onSignUp, loading, error }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSignUp(email, password);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="signup-email" className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Email</Label>
        <Input
          id="signup-email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-10"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="signup-password" className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Password</Label>
        <div className="relative">
          <Input
            id="signup-password"
            type={showPassword ? "text" : "password"}
            required
            autoComplete="new-password"
            minLength={8}
            placeholder="Min. 8 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-10 pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {password.length > 0 && (
          <div className="flex items-center gap-3 text-[11px] mt-1">
            <span className={`inline-flex items-center gap-1 ${password.length >= 8 ? "text-emerald-400" : "text-muted-foreground/60"}`}>
              {password.length >= 8 ? <Check className="h-3 w-3" /> : <XIcon className="h-3 w-3" />}
              8+ chars
            </span>
            <span className={`inline-flex items-center gap-1 ${/[A-Z]/.test(password) ? "text-emerald-400" : "text-muted-foreground/60"}`}>
              {/[A-Z]/.test(password) ? <Check className="h-3 w-3" /> : <XIcon className="h-3 w-3" />}
              Uppercase
            </span>
            <span className={`inline-flex items-center gap-1 ${/[0-9!@#$%^&*]/.test(password) ? "text-emerald-400" : "text-muted-foreground/60"}`}>
              {/[0-9!@#$%^&*]/.test(password) ? <Check className="h-3 w-3" /> : <XIcon className="h-3 w-3" />}
              Number/Symbol
            </span>
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="signup-confirm" className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Confirm password</Label>
        <div className="relative">
          <Input
            id="signup-confirm"
            type={showConfirm ? "text" : "password"}
            required
            autoComplete="new-password"
            minLength={8}
            placeholder="Re-enter password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="h-10 pr-10"
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
            tabIndex={-1}
            aria-label={showConfirm ? "Hide password" : "Show password"}
          >
            {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {confirmPassword.length > 0 && (
          <p className={`text-[11px] inline-flex items-center gap-1 mt-0.5 ${
            password === confirmPassword ? "text-emerald-400" : "text-destructive"
          }`}>
            {password === confirmPassword
              ? <><Check className="h-3 w-3" /> Passwords match</>
              : <><XIcon className="h-3 w-3" /> Passwords don't match</>}
          </p>
        )}
      </div>
      <Button
        type="submit"
        disabled={loading || (confirmPassword.length > 0 && password !== confirmPassword)}
        className="w-full gap-2 h-10"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
        Create account
      </Button>
      {error && <p className="text-sm text-destructive font-mono" role="alert">{error}</p>}
    </form>
  );
}
